import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import LoginPage from "../Login/LoginPage";
import {
  Table,
  Card,
  Space,
  Typography,
  Empty,
  Button,
} from "antd";
import { useSelector } from "react-redux";
import { isLogged, getAuthToken } from "../../helpers/account";
import { api } from "../../config/api";
import { GiftOutlined, EyeOutlined } from "@ant-design/icons";
import NewRewardForm from "./Account/Reward/Forms/NewRewardForm";
import EditRewardForm from "./Account/Reward/Forms/EditRewardForm";
import { Helmet } from "react-helmet-async";

const { Title, Text } = Typography;

function RewardsPage() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const bankAccountsListUpdated = useSelector(
    (state) => state.bankAccountsListUpdated
  );

  const columns = [
    {
      title: t('dashboard.rewards.giftCard'),
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div style={{ padding: "12px 0" }}>
          <div style={{ marginBottom: "8px" }}>
            <Text strong style={{ fontSize: "16px", color: "#2c3e50" }}>
              {name}
            </Text>
          </div>
          <div style={{ fontSize: "14px", color: "#667eea", fontWeight: "500" }}>
            {record.value} zł
          </div>
        </div>
      ),
    },
    {
      title: t('transactions.actions'),
      key: "actions",
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <EditRewardForm reward={{ uuid: record.uuid }} />
      ),
    },
  ];

  useEffect(() => {
    const apiUrl = `${api.host}/rewards`;

    if (isLogged()) {
      fetch(apiUrl, {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `${getAuthToken()}`,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          setData(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching rewards data:", error);
          setLoading(false);
        });
    }
  }, [bankAccountsListUpdated]);

  if (isLogged()) {
    return (
      <>
        <Helmet>
          <title>{t('dashboard.rewards.title')}</title>
        </Helmet>
        
        <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "40px 20px" }}>
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            {/* Header */}
            <div style={{ marginBottom: "32px" }}>
              <Title level={2} style={{ margin: 0, color: "#2c3e50" }}>
                {t('dashboard.rewards.giftCards')}
              </Title>
              <Text style={{ color: "#6c757d", fontSize: "16px" }}>
                {t('dashboard.rewards.manageGiftCards')}
              </Text>
            </div>

            {/* New Reward Button */}
            <div style={{ marginBottom: "24px" }}>
              <NewRewardForm />
            </div>

            {/* Rewards Table */}
            <Card
              style={{
                border: "none",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                borderRadius: "12px",
              }}
              bodyStyle={{ padding: 0 }}
            >
              <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                rowKey="uuid"
                scroll={{ x: 400 }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  showQuickJumper: false,
                  style: { padding: "16px 24px" },
                  simple: window.innerWidth < 768
                }}
                locale={{
                  emptyText: (
                    <Empty
                      image={
                        <div style={{ fontSize: "64px", color: "#dee2e6" }}>
                          🎁
                        </div>
                      }
                      description={
                        <div style={{ textAlign: "center", padding: "40px 20px" }}>
                          <Title level={4} style={{ color: "#6c757d", marginBottom: "16px" }}>
                            {t('dashboard.rewards.noGiftCards')}
                          </Title>
                          <Text style={{ color: "#6c757d", fontSize: "16px", display: "block", marginBottom: "24px" }}>
                            {t('dashboard.rewards.addFirstCard')}
                          </Text>
                        </div>
                      }
                    />
                  ),
                }}
                style={{
                  ".ant-table-thead > tr > th": {
                    background: "#f8f9fa",
                    borderBottom: "1px solid #e9ecef",
                    color: "#495057",
                    fontWeight: "600",
                  }
                }}
              />
            </Card>
          </div>
        </div>
      </>
    );
  } else {
    return <LoginPage />;
  }
}

export default RewardsPage;